import React from 'react';
import { productName, logoSrc, privacyUrl, termsUrl } from '../brand';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import PageFooter from '../components/chrome/PageFooter';

// Sign-up page (FR-050..052). Two-column layout from the prototype.
// Google SSO ONLY per FR-002 / FR-050 — no email + password form.
// Org / role / terms acceptance is collected post-SSO via the FR-045
// profile completion screen, not inline here, so the form column is
// just the SSO button + terms-adjacent disclaimer.
//
// Right-column "What you get" items are taken verbatim from FR-051's
// canonical examples, plus a fourth V1 capability. None of the V2 /
// out-of-spec phrasings ("monthly refresh", "negotiation playbook",
// "analyst review", "driver-level breakdown") appear.

const VALUE_ITEMS = [
  {
    title: 'A defensible number',
    body: 'Calibrated on 55+ documented data partnerships, from large pharma deals to academic biobanks.',
  },
  {
    title: 'Tier-anchored pricing',
    body: 'Six bands grounded in real deals — Entry through Ultra-Premium, each with a base annual pharma fee.',
  },
  {
    title: 'Reference deal comparison',
    body: 'See how your dataset stacks against FinnGen, UK Biobank, and 50+ others, with a per-patient comparison.',
  },
  {
    title: 'PDF leave-behind',
    body: 'One click to a branded summary you can hand a prospect — inputs, outputs, methodology link.',
  },
];

export default function SignUp({ onContinueWithGoogle, onSignUpWithEmail, onSignIn, onLanding }) {
  return (
    <div style={pageStyle}>
      <div style={twoColStyle}>
        <div style={leftPanelStyle}>
          <button type="button" onClick={onLanding} style={logoButtonStyle} aria-label="Back to landing">
            <img src={logoSrc} alt={productName} style={logoStyle} />
          </button>

          <div style={formStackStyle}>
            <div>
              <div style={eyebrowStyle}>Create account</div>
              <h1 style={heroStyle}>
                Two minutes. Then your first <em style={accentItalicStyle}>valuation.</em>
              </h1>
            </div>

            <GoogleSignInButton onClick={onContinueWithGoogle} />

            {onSignUpWithEmail && (
              <button type="button" onClick={onSignUpWithEmail} style={emailButtonStyle}>
                Or sign up with email
              </button>
            )}

            <p style={termsLineStyle}>
              By continuing you agree to our{' '}
              {termsUrl
                ? <a href={termsUrl} style={termsLinkStyle} target="_blank" rel="noopener noreferrer">Terms</a>
                : <span style={termsLinkMutedStyle}>Terms</span>}
              {' '}and{' '}
              <a href={privacyUrl} style={termsLinkStyle} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>

            <p style={altStyle}>
              Already have an account?{' '}
              <button type="button" onClick={onSignIn} style={inlineLinkStyle}>Sign in</button>
            </p>
          </div>
        </div>

        <div style={rightPanelStyle}>
          <div style={{ ...eyebrowStyle, color: 'var(--amber-strong)', marginBottom: '20px' }}>What you get</div>
          <ol style={valueListStyle}>
            {VALUE_ITEMS.map((item, i) => (
              <li key={item.title} style={valueItemStyle}>
                <span style={valueNumberStyle}>0{i + 1}</span>
                <div>
                  <div style={valueTitleStyle}>{item.title}</div>
                  <div style={valueBodyStyle}>{item.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

const pageStyle = { minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)', color: 'var(--ink)', display: 'flex', flexDirection: 'column' };
const twoColStyle = { display: 'grid', gridTemplateColumns: 'minmax(420px, 1fr) 1fr', flex: 1, maxWidth: '1280px', margin: '0 auto', width: '100%' };

const leftPanelStyle = { padding: '48px 56px', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--line)' };
const logoButtonStyle = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', alignSelf: 'flex-start' };
const logoStyle = { height: '48px', width: 'auto', display: 'block' };

const formStackStyle = { marginTop: '64px', display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '460px' };

const eyebrowStyle = { fontSize: '13px', color: 'var(--accent)', fontWeight: 500 };
const heroStyle = {
  fontFamily: 'var(--display)',
  fontWeight: 500,
  fontSize: '44px',
  letterSpacing: '-0.02em',
  lineHeight: 1.05,
  margin: '8px 0 0',
  color: 'var(--ink)',
};
const accentItalicStyle = { fontStyle: 'italic', color: 'var(--accent)' };

const termsLineStyle = { fontSize: '12px', color: 'var(--muted)', margin: 0, lineHeight: 1.55 };
const termsLinkStyle = { color: 'var(--ink2)', textDecoration: 'underline' };
const termsLinkMutedStyle = { color: 'var(--muted)' };

const altStyle = { fontSize: '13px', color: 'var(--muted)', margin: 0 };
const inlineLinkStyle = { background: 'transparent', border: 'none', color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', padding: 0 };

const rightPanelStyle = { padding: '88px 56px 48px', display: 'flex', flexDirection: 'column' };
const valueListStyle = { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column' };
const valueItemStyle = {
  display: 'grid',
  gridTemplateColumns: '64px 1fr',
  gap: '12px',
  padding: '18px 0',
  borderBottom: '1px solid var(--line)',
  alignItems: 'baseline',
};
const valueNumberStyle = {
  fontFamily: 'var(--display)',
  fontStyle: 'italic',
  fontSize: '20px',
  color: 'var(--amber-strong)',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 500,
};
const valueTitleStyle = { fontSize: '16px', fontWeight: 600, color: 'var(--ink)' };
const valueBodyStyle = { fontSize: '13.5px', color: 'var(--ink2)', marginTop: '4px', lineHeight: 1.55, maxWidth: '380px' };

const emailButtonStyle = {
  background: 'transparent',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--ink2)',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '14px',
  padding: '10px 16px',
  textAlign: 'center',
  width: '100%',
};
